// ============================================================
// FavouriteStar.tsx
// Reusable star button that pins/unpins a case, client(-section), or task
// to the sidebar Favourites (Stage 2). FILLED = favourited, OUTLINE = not.
// Toggling calls the store's toggleFavourite, which hits the live add/
// remove endpoints and updates the cached favourites slice — so the star
// flips instantly (optimistic; reverts on API failure).
//
// Reads the favourited state reactively from the store (subscribes to the
// favourites slice via a boolean selector), so it re-renders the moment
// the cache changes — including when toggled elsewhere for the same item.
//
// variant:
//   'dark'  — on the dark case header (bg-casevo-header): text-casevo-light
//             → hover white, matching NotificationsBell.
//   'light' — on light client/task headers: text-casevo-mid → hover
//             text-casevo-forest.
// When FILLED the star shows in amber (text-amber-400) in both variants so
// "pinned" reads at a glance regardless of header background.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import type { FavouriteItemType } from '../../api/casevoApi'

interface FavouriteStarProps {
  itemType: FavouriteItemType
  itemId: string
  // Cached display label stored on the favourite (rendered in the sidebar).
  label: string
  // Optional deep-link sub-target (e.g. the client's current section key).
  subSection?: string | null
  variant?: 'dark' | 'light'
  className?: string
  // Optional sizing/tone overrides (My work rows use a 14px star in the
  // border tone, gold when on — per the mockup). Defaults = unchanged.
  size?: number
  idleClassName?: string
  filledClassName?: string
}

const FavouriteStar: React.FC<FavouriteStarProps> = ({
  itemType,
  itemId,
  label,
  subSection = null,
  variant = 'light',
  className = '',
  size = 18,
  idleClassName,
  filledClassName,
}) => {
  // Reactive boolean — re-runs whenever the favourites slice changes, so
  // the fill flips as soon as toggleFavourite mutates the cache.
  const favourited = useCasevoStore((s) => s.isFavourited(itemType, itemId))
  const toggleFavourite = useCasevoStore((s) => s.toggleFavourite)

  const handleClick = (e: React.MouseEvent): void => {
    // Stop the click bubbling to a surrounding row/header handler (e.g. a
    // case row's open-on-click) — starring must not also navigate.
    e.stopPropagation()
    void toggleFavourite({ itemType, itemId, label, subSection })
  }

  // Idle colour by variant; filled overrides to amber in both.
  const idleColour =
    variant === 'dark'
      ? 'text-casevo-light hover:text-white'
      : 'text-casevo-mid hover:text-casevo-forest'
  const colour = favourited
    ? (filledClassName ?? 'text-amber-400 hover:text-amber-400')
    : (idleClassName ?? idleColour)

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={favourited}
      aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
      title={favourited ? 'Remove from favourites' : 'Add to favourites'}
      className={`p-2 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-light shrink-0 ${colour} ${className}`}
    >
      {/* Hand-rolled 5-point star. fill follows favourited state; stroke is
          always currentColor so the outline reads on either background. */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={favourited ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  )
}

export default FavouriteStar
